/* eslint-disable react-refresh/only-export-components */
/**
 * Authentication Context
 *
 * Manages user authentication state and provides auth methods
 */

import React, { createContext, useContext, useEffect, useState } from 'react'
import { api } from '@/api/client'
import { API_ROUTES, TOKEN_STORAGE_KEY } from '@/lib/constants'
import type { User, TokenResponse } from '@/api/types'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<User>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  /**
   * Get current user from API
   */
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY)
      if (!token) {
        setIsLoading(false)
        return
      }

      const currentUser = await api.get<User>('/auth/me')
      setUser(currentUser)
    } catch (error) {
      // Only clear the session on definitive auth failures (401/403).
      // Network aborts (ERR_ABORTED) and transient errors must NOT log the user out.
      const status = (error as { status?: number })?.status
      if (status === 401 || status === 403) {
        localStorage.removeItem(TOKEN_STORAGE_KEY)
        setUser(null)
      } else {
        console.warn('Could not refresh user (transient error), keeping session:', error)
      }
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Login with email and password
   */
  const login = async (email: string, password: string): Promise<User> => {
    setIsLoading(true)
    try {
      const formData = new URLSearchParams()
      formData.append('username', email)
      formData.append('password', password)

      const response = await api.post<TokenResponse>(API_ROUTES.AUTH.TOKEN, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })

      localStorage.setItem(TOKEN_STORAGE_KEY, response.access_token)

      // Fetch user details
      const currentUser = await api.get<User>('/auth/me')
      setUser(currentUser)
      setIsLoading(false)
      return currentUser
    } catch (error) {
      setIsLoading(false)
      throw error
    }
  }

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      await api.post(API_ROUTES.AUTH.LOGOUT)
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      setUser(null)
    }
  }

  /**
   * Refresh current user data
   */
  const refreshUser = async () => {
    await fetchCurrentUser()
  }

  /**
   * Initialize auth state on mount
   */
  useEffect(() => {
    fetchCurrentUser()
  }, [])

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * useAuth hook
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
